import { useMemo, useState } from "react";
import { useData } from "@/context/DataContext";
import { isoDate, addDays, MONTH_NAMES, WEEKDAY_SHORT, formatTime } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { deleteWithUndo } from "@/lib/undo";

const IMP_STYLES = {
  exam: { bg: "bg-rose-500/15", text: "text-rose-300", border: "border-rose-500/30", label: "Toets" },
  deadline: { bg: "bg-amber-500/15", text: "text-amber-300", border: "border-amber-500/30", label: "Deadline" },
  shift: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/30", label: "Dienst" },
  appointment: { bg: "bg-blue-500/15", text: "text-blue-300", border: "border-blue-500/30", label: "Afspraak" },
  birthday: { bg: "bg-pink-500/15", text: "text-pink-300", border: "border-pink-500/30", label: "Verjaardag" },
  trip: { bg: "bg-teal-500/15", text: "text-teal-300", border: "border-teal-500/30", label: "Reis" },
  concert: { bg: "bg-violet-500/15", text: "text-violet-300", border: "border-violet-500/30", label: "Concert" },
  other: { bg: "bg-slate-500/15", text: "text-slate-300", border: "border-slate-500/30", label: "Overig" },
};

export default function MonthPage() {
  const { events, importantDates, catById, deleteImportant, restoreImportant } = useData();
  const [anchor, setAnchor] = useState(new Date());
  const [selected, setSelected] = useState(null);

  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const first = new Date(year, month, 1);
  const startWeekday = (first.getDay() + 6) % 7; // Monday index
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < startWeekday; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [startWeekday, daysInMonth, year, month]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [events]);

  const importantByDate = useMemo(() => {
    const map = {};
    importantDates.forEach((i) => { (map[i.date] = map[i.date] || []).push(i); });
    return map;
  }, [importantDates]);

  const selectedIso = selected ? isoDate(selected) : null;
  const dayItems = selectedIso ? {
    events: eventsByDate[selectedIso] || [],
    important: importantByDate[selectedIso] || [],
  } : null;

  return (
    <div className="p-5 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Maandoverzicht</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 mt-1">
            {MONTH_NAMES[month].charAt(0).toUpperCase() + MONTH_NAMES[month].slice(1)} {year}
          </h1>
          <div className="text-sm text-slate-500 mt-1">Belangrijke data & grote afspraken</div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="month-prev" variant="outline" size="icon"
            onClick={() => setAnchor(new Date(year, month - 1, 1))} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-300">
            <CaretLeft size={16} />
          </Button>
          <Button data-testid="month-today" variant="outline"
            onClick={() => setAnchor(new Date())} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-200 text-xs">
            Deze maand
          </Button>
          <Button data-testid="month-next" variant="outline" size="icon"
            onClick={() => setAnchor(new Date(year, month + 1, 1))} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-300">
            <CaretRight size={16} />
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#121C2F] overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-800 bg-slate-900/40">
          {WEEKDAY_SHORT.map((w) => (
            <div key={w} className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, idx) => {
            if (!d) {
              return <div key={`blank-${idx}`} className="min-h-[112px] border-b border-r border-slate-800/70 bg-slate-900/20" />;
            }
            const iso = isoDate(d);
            const isToday = iso === isoDate(new Date());
            const evs = eventsByDate[iso] || [];
            const imps = importantByDate[iso] || [];
            const badges = [...imps.slice(0, 2), ...evs.slice(0, 2 - Math.min(imps.length, 2))];
            const totalItems = evs.length + imps.length;
            return (
              <button
                key={iso}
                onClick={() => setSelected(d)}
                data-testid={`month-cell-${iso}`}
                className={`text-left min-h-[112px] border-b border-r border-slate-800/70 p-2 transition-colors hover:bg-slate-800/30 ${
                  isToday ? "bg-blue-500/5" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className={`text-sm font-semibold ${isToday ? "text-blue-300" : "text-slate-200"}`}>
                    {d.getDate()}
                  </div>
                  {totalItems > 0 && (
                    <div className="text-[10px] text-slate-500">{totalItems}</div>
                  )}
                </div>
                <div className="mt-1.5 space-y-1">
                  {imps.slice(0, 2).map((i) => {
                    const s = IMP_STYLES[i.kind] || IMP_STYLES.other;
                    return (
                      <div key={i.id} className={`text-[10px] px-1.5 py-0.5 rounded border ${s.bg} ${s.text} ${s.border} truncate`}>
                        {i.title}
                      </div>
                    );
                  })}
                  {evs.slice(0, Math.max(0, 2 - imps.slice(0, 2).length)).map((e) => {
                    const c = catById(e.category_id);
                    const color = c?.color || "#3B82F6";
                    return (
                      <div key={e.id} className="text-[10px] px-1.5 py-0.5 rounded truncate text-slate-200"
                        style={{ background: `${color}22`, borderLeft: `2px solid ${color}` }}>
                        {e.title}
                      </div>
                    );
                  })}
                  {totalItems > badges.length && (
                    <div className="text-[10px] text-slate-500 pl-1.5">+{totalItems - badges.length} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-[#121C2F] border-slate-800 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50">
              {selected?.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[60vh] overflow-y-auto">
            {dayItems?.important.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Belangrijk</div>
                <div className="space-y-2">
                  {dayItems.important.map((i) => {
                    const s = IMP_STYLES[i.kind] || IMP_STYLES.other;
                    return (
                      <div key={i.id} className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${s.bg} ${s.border}`}>
                        <div>
                          <div className={`text-sm font-medium ${s.text}`}>{i.title}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5">{s.label}{i.notes ? ` · ${i.notes}` : ""}</div>
                        </div>
                        <button
                          data-testid={`important-delete-${i.id}`}
                          onClick={() => deleteWithUndo({
                            label: `"${i.title}" verwijderd`,
                            deleteFn: async () => { await deleteImportant(i.id); },
                            restoreFn: async () => { await restoreImportant(i); },
                          })}
                          className="text-slate-500 hover:text-rose-400 text-xs"
                        >Verwijderen</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {dayItems?.events.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Agenda</div>
                <div className="space-y-2">
                  {dayItems.events.map((e) => {
                    const c = catById(e.category_id);
                    const color = c?.color || "#3B82F6";
                    return (
                      <div key={e.id} className="rounded-lg border border-slate-800 p-3"
                        style={{ background: `${color}12`, borderLeftWidth: 3, borderLeftColor: color }}>
                        <div className="text-sm font-medium text-slate-100">{e.title}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {formatTime(e.start_time)} – {formatTime(e.end_time)}
                          {c && <span className="ml-2" style={{ color }}>· {c.name}</span>}
                        </div>
                        {e.location && <div className="text-[11px] text-slate-500 mt-1">📍 {e.location}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {dayItems && dayItems.important.length === 0 && dayItems.events.length === 0 && (
              <div className="text-center text-sm text-slate-500 py-8">Niets ingepland.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
